// AdSense 調試控制台工具
// 在瀏覽器控制台中提供強大的廣告調試功能

export class AdDebugConsole {
  private static instance: AdDebugConsole
  private logHistory: any[] = []

  public static getInstance(): AdDebugConsole {
    if (!AdDebugConsole.instance) {
      AdDebugConsole.instance = new AdDebugConsole()
    }
    return AdDebugConsole.instance
  }

  // 初始化調試控制台
  initializeDebugConsole() {
    if (typeof window === 'undefined') return

    // 添加全域調試方法
    (window as any).AdDebug = {
      // 基本診斷
      diagnose: () => this.comprehensiveDiagnose(),
      
      // 檢查廣告狀態
      checkAds: () => this.checkAllAds(),
      
      // 強制重載廣告
      reloadAds: () => this.forceReloadAllAds(),
      
      // 模擬廣告載入
      simulate: (adSlot: string) => this.simulateAdLoad(adSlot),
      
      // 測試容器
      testContainers: () => this.testAllContainers(),
      
      // 清理廣告
      cleanup: () => this.cleanupAllAds(),
      
      // 設置調試模式
      debug: (enabled: boolean = true) => this.setDebugMode(enabled),
      
      // 顯示幫助
      help: () => this.showHelp(),
      
      // 廣告性能報告
      performance: () => this.generatePerformanceReport(),
      
      // 修復常見問題
      fix: {
        containers: () => this.fixContainerSizes(),
        scripts: () => this.fixAdSenseScripts(),
        positioning: () => this.fixAdPositioning()
      }
    }

    this.log('✅ AdSense 調試控制台已初始化', 'success')
    this.log('💡 使用 window.AdDebug.help() 查看所有可用命令', 'info')
  }

  // 綜合診斷
  comprehensiveDiagnose() {
    console.group('🔍 AdSense 綜合診斷報告')
    
    this.log('開始綜合診斷...', 'info')
    
    // 1. 環境檢查
    console.group('🌍 環境檢查')
    this.checkEnvironment()
    console.groupEnd()
    
    // 2. 腳本狀態
    console.group('📦 AdSense 腳本狀態')
    this.checkAdSenseScript()
    console.groupEnd()
    
    // 3. 廣告元素檢查
    console.group('📱 廣告元素檢查')
    this.checkAdElements()
    console.groupEnd()
    
    // 4. 容器尺寸檢查
    console.group('📏 容器尺寸檢查')
    this.checkContainerSizes()
    console.groupEnd()
    
    // 5. 建議修復方案
    console.group('💡 建議修復方案')
    this.provideSuggestions()
    console.groupEnd()
    
    console.groupEnd()
    
    return {
      environment: this.getEnvironmentInfo(),
      scripts: this.getScriptStatus(),
      ads: this.getAdElementsInfo(),
      containers: this.getContainerInfo(),
      suggestions: this.getSuggestions()
    }
  }

  // 環境檢查
  private checkEnvironment() {
    const env = {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      userAgent: navigator.userAgent,
      isDev: import.meta.env.DEV,
      isProd: import.meta.env.PROD,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      adBlocker: this.detectAdBlocker()
    }
    
    console.log('🌍 環境資訊:', env)
    
    if (env.hostname === 'localhost') {
      this.log('⚠️ 在 localhost 環境，AdSense 可能不會顯示真實廣告', 'warning')
    }
    
    if (env.protocol !== 'https:' && env.hostname !== 'localhost') {
      this.log('⚠️ 非 HTTPS 環境可能影響 AdSense 載入', 'warning')
    }
    
    if (env.adBlocker) {
      this.log('🚫 檢測到廣告阻擋器，這可能是廣告無法顯示的原因', 'error')
    }
    
    return env
  }

  // AdSense 腳本檢查
  private checkAdSenseScript() {
    const script = document.querySelector('script[src*="adsbygoogle.js"]')
    const hasArray = !!(window as any).adsbygoogle
    const arrayLength = hasArray ? (window as any).adsbygoogle.length : 0
    
    const status = {
      scriptExists: !!script,
      scriptSrc: script?.getAttribute('src') || null,
      arrayExists: hasArray,
      arrayLength: arrayLength,
      loadStatus: script?.getAttribute('data-loaded') || 'unknown'
    }
    
    console.log('📦 AdSense 腳本狀態:', status)
    
    if (!status.scriptExists) {
      this.log('❌ AdSense 腳本未找到', 'error')
    } else if (!status.arrayExists) {
      this.log('⚠️ AdSense 腳本已載入但 adsbygoogle 陣列不存在', 'warning')
    } else {
      this.log(`✅ AdSense 腳本正常，佇列長度: ${arrayLength}`, 'success')
    }
    
    return status
  }

  // 廣告元素檢查
  private checkAdElements() {
    const adElements = document.querySelectorAll('.adsbygoogle')
    const adUnits = document.querySelectorAll('[class*="ad-unit"], [class*="ad-"]')
    
    console.log(`📱 找到 ${adElements.length} 個 AdSense 元素，${adUnits.length} 個廣告容器`)
    
    const elementInfo: any[] = []
    
    adElements.forEach((element, index) => {
      const el = element as HTMLElement
      const rect = el.getBoundingClientRect()
      const status = el.getAttribute('data-adsbygoogle-status')
      const client = el.getAttribute('data-ad-client')
      const slot = el.getAttribute('data-ad-slot')
      
      const info = {
        index: index + 1,
        status: status || '未初始化',
        client: client,
        slot: slot,
        dimensions: `${rect.width}x${rect.height}`,
        visible: rect.width > 0 && rect.height > 0,
        inViewport: this.isInViewport(el),
        hasContent: el.innerHTML.trim().length > 0,
        element: el
      }
      
      elementInfo.push(info)
      
      console.log(`廣告 ${index + 1}:`, info)
      
      // 診斷問題
      if (rect.width === 0) {
        this.log(`⚠️ 廣告 ${index + 1} 寬度為 0`, 'warning')
      }
      if (!status) {
        this.log(`⚠️ 廣告 ${index + 1} 未初始化`, 'warning')
      }
      if (status === 'error') {
        this.log(`❌ 廣告 ${index + 1} 載入失敗`, 'error')
      }
    })
    
    return elementInfo
  }

  // 容器尺寸檢查
  private checkContainerSizes() {
    const containers = document.querySelectorAll('[class*="ad-unit"], [class*="ad-container"]')
    
    containers.forEach((container, index) => {
      const el = container as HTMLElement
      const rect = el.getBoundingClientRect()
      const computedStyle = window.getComputedStyle(el)
      
      const info = {
        index: index + 1,
        dimensions: `${rect.width}x${rect.height}`,
        minWidth: computedStyle.minWidth,
        minHeight: computedStyle.minHeight,
        display: computedStyle.display,
        position: computedStyle.position,
        overflow: computedStyle.overflow
      }
      
      console.log(`容器 ${index + 1}:`, info)
      
      if (rect.width < 200) {
        this.log(`⚠️ 容器 ${index + 1} 寬度過小 (${rect.width}px)`, 'warning')
      }
      if (rect.height < 50) {
        this.log(`⚠️ 容器 ${index + 1} 高度過小 (${rect.height}px)`, 'warning')
      }
    })
  }

  // 輔助方法
  private detectAdBlocker(): boolean {
    const testElement = document.createElement('div')
    testElement.innerHTML = '&nbsp;'
    testElement.className = 'adsbox'
    testElement.style.cssText = 'position:absolute;left:-999px;'
    document.body.appendChild(testElement)
    
    const isBlocked = testElement.offsetHeight === 0
    document.body.removeChild(testElement)
    
    return isBlocked
  }

  private isInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect()
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.right <= window.innerWidth
    )
  }

  private log(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info') {
    const timestamp = new Date().toLocaleTimeString()
    const logEntry = { timestamp, message, type }
    
    this.logHistory.push(logEntry)
    
    const styles = {
      info: 'color: #3b82f6',
      success: 'color: #10b981',
      warning: 'color: #f59e0b',
      error: 'color: #ef4444'
    }
    
    console.log(`%c[AdDebug ${timestamp}] ${message}`, styles[type])
  }

  private setDebugMode(enabled: boolean) {
    this.log(`調試模式 ${enabled ? '已啟用' : '已禁用'}`, 'info')
  }

  private showHelp() {
    console.log(`
🔧 AdSense 調試控制台 - 可用命令:

基本診斷:
  AdDebug.diagnose()      - 執行完整診斷
  AdDebug.checkAds()      - 檢查所有廣告狀態
  AdDebug.performance()   - 生成性能報告

修復工具:
  AdDebug.fix.containers()   - 修復容器尺寸問題
  AdDebug.fix.scripts()      - 修復 AdSense 腳本問題
  AdDebug.fix.positioning()  - 修復廣告定位問題

測試工具:
  AdDebug.reloadAds()        - 強制重載所有廣告
  AdDebug.simulate('slot')   - 模擬廣告載入
  AdDebug.testContainers()   - 測試容器設置

設置:
  AdDebug.debug(true/false)  - 啟用/禁用調試模式
  AdDebug.cleanup()          - 清理所有廣告

💡 建議先執行 AdDebug.diagnose() 來獲得完整的問題分析
    `)
  }

  // 獲取各種資訊的方法
  private getEnvironmentInfo() {
    return {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      isDev: import.meta.env.DEV,
      viewport: { width: window.innerWidth, height: window.innerHeight }
    }
  }

  private getScriptStatus() {
    const script = document.querySelector('script[src*="adsbygoogle.js"]')
    return {
      exists: !!script,
      array: !!(window as any).adsbygoogle,
      length: (window as any).adsbygoogle?.length || 0
    }
  }

  private getAdElementsInfo() {
    const elements = document.querySelectorAll('.adsbygoogle')
    return Array.from(elements).map((el, index) => ({
      index: index + 1,
      status: el.getAttribute('data-adsbygoogle-status'),
      dimensions: `${el.getBoundingClientRect().width}x${el.getBoundingClientRect().height}`
    }))
  }

  private getContainerInfo() {
    const containers = document.querySelectorAll('[class*="ad-"]')
    return Array.from(containers).map((el, index) => ({
      index: index + 1,
      dimensions: `${el.getBoundingClientRect().width}x${el.getBoundingClientRect().height}`
    }))
  }

  private getSuggestions() {
    // 返回建議修復方案
    return [
      '檢查容器尺寸是否足夠',
      '確認 AdSense 腳本正確載入',
      '檢查廣告位 ID 是否正確',
      '確認網域已添加到 AdSense 帳戶'
    ]
  }

  // 生成性能報告
  generatePerformanceReport() {
    console.group('📊 AdSense 性能報告')
    
    const report = {
      loadTime: this.measureLoadTime(),
      renderTime: this.measureRenderTime(),
      memoryUsage: this.getMemoryUsage(),
      recommendations: this.getPerformanceRecommendations()
    }
    
    console.log('載入時間:', report.loadTime)
    console.log('渲染時間:', report.renderTime)
    console.log('記憶體使用:', report.memoryUsage)
    console.log('性能建議:', report.recommendations)
    
    console.groupEnd()
    return report
  }

  private measureLoadTime() {
    // 簡化的載入時間測量
    return 'AdSense 腳本載入時間監控需要在載入前設置'
  }

  private measureRenderTime() {
    // 測量廣告渲染時間
    const adElements = document.querySelectorAll('.adsbygoogle')
    return `${adElements.length} 個廣告元素`
  }

  private getMemoryUsage() {
    if ('memory' in performance) {
      const memory = (performance as any).memory
      return {
        used: `${Math.round(memory.usedJSHeapSize / 1024 / 1024)}MB`,
        total: `${Math.round(memory.totalJSHeapSize / 1024 / 1024)}MB`
      }
    }
    return '記憶體資訊不可用'
  }

  private getPerformanceRecommendations() {
    return [
      '使用懶載入減少初始載入時間',
      '合理配置廣告密度',
      '避免在關鍵渲染路徑中載入廣告',
      '使用適當的廣告尺寸'
    ]
  }

  // 清理所有廣告
  cleanupAllAds() {
    const adElements = document.querySelectorAll('.adsbygoogle')
    adElements.forEach(el => {
      el.remove()
    })
    this.log(`清理了 ${adElements.length} 個廣告元素`, 'success')
  }

  // 檢查所有廣告
  checkAllAds() {
    const elements = this.checkAdElements()
    console.table(elements)
    return elements
  }

  // 測試所有容器
  testAllContainers() {
    this.checkContainerSizes()
    return this.getContainerInfo()
  }

  // 模擬廣告載入
  simulateAdLoad(adSlot: string) {
    this.log(`🧪 模擬載入廣告位: ${adSlot}`, 'info')
    
    const testContainer = document.createElement('div')
    testContainer.innerHTML = `
      <ins class="adsbygoogle"
           style="display:block;width:300px;height:250px;"
           data-ad-client="ca-pub-5096069099939529"
           data-ad-slot="${adSlot}"></ins>
    `
    
    document.body.appendChild(testContainer)
    
    if ((window as any).adsbygoogle) {
      try {
        (window as any).adsbygoogle.push({})
        this.log('✅ 模擬廣告已推送', 'success')
      } catch (error) {
        this.log('❌ 模擬廣告載入失敗: ' + error, 'error')
      }
    }
    
    // 5秒後檢查結果
    setTimeout(() => {
      const adElement = testContainer.querySelector('.adsbygoogle')
      const status = adElement?.getAttribute('data-adsbygoogle-status')
      this.log(`📊 模擬廣告狀態: ${status || '無狀態'}`, 'info')
      
      // 清理測試元素
      setTimeout(() => {
        testContainer.remove()
        this.log('🗑️ 清理測試廣告元素', 'info')
      }, 5000)
    }, 5000)
  }

  // 修復 AdSense 腳本
  fixAdSenseScripts() {
    if (document.querySelector('script[src*="adsbygoogle.js"]')) {
      this.log('✅ AdSense 腳本已存在', 'success')
      return
    }
    
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5096069099939529'
    script.crossOrigin = 'anonymous'
    
    script.onload = () => {
      (window as any).adsbygoogle = (window as any).adsbygoogle || []
      this.log('✅ AdSense 腳本載入成功', 'success')
    }
    
    script.onerror = () => {
      this.log('❌ AdSense 腳本載入失敗', 'error')
    }
    
    document.head.appendChild(script)
    this.log('🔄 正在載入 AdSense 腳本...', 'info')
  }

  // 修復廣告定位
  fixAdPositioning() {
    const containers = document.querySelectorAll('[class*="ad-"]')
    let fixedCount = 0
    
    containers.forEach((container, index) => {
      const el = container as HTMLElement
      const computedStyle = window.getComputedStyle(el)
      
      if (computedStyle.position === 'absolute' && computedStyle.left === '-999px') {
        el.style.position = 'relative'
        el.style.left = 'auto'
        fixedCount++
        this.log(`✅ 修復容器 ${index + 1} 定位`, 'success')
      }
    })
    
    this.log(`總共修復了 ${fixedCount} 個定位問題`, 'success')
    return fixedCount
  }

  // 提供修復建議
  private provideSuggestions() {
    const suggestions = []
    
    // 檢查常見問題並提供建議
    const script = document.querySelector('script[src*="adsbygoogle.js"]')
    if (!script) {
      suggestions.push('載入 AdSense 腳本: window.AdDebug.fix.scripts()')
    }
    
    const zeroWidthContainers = Array.from(document.querySelectorAll('.adsbygoogle')).filter(
      el => (el as HTMLElement).getBoundingClientRect().width === 0
    )
    if (zeroWidthContainers.length > 0) {
      suggestions.push('修復容器尺寸: window.AdDebug.fix.containers()')
    }
    
    if (this.detectAdBlocker()) {
      suggestions.push('請暫時禁用廣告阻擋器以測試廣告顯示')
    }
    
    suggestions.forEach(suggestion => {
      this.log(`💡 建議: ${suggestion}`, 'info')
    })
    
    return suggestions
  }

  // 修復容器尺寸
  fixContainerSizes() {
    const containers = document.querySelectorAll('.adsbygoogle')
    let fixedCount = 0
    
    containers.forEach((container, index) => {
      const el = container as HTMLElement
      const rect = el.getBoundingClientRect()
      
      if (rect.width < 200 || rect.height < 50) {
        el.style.minWidth = '200px'
        el.style.minHeight = '100px'
        el.style.display = 'block'
        
        fixedCount++
        this.log(`✅ 修復容器 ${index + 1} 尺寸`, 'success')
      }
    })
    
    this.log(`總共修復了 ${fixedCount} 個容器`, 'success')
    return fixedCount
  }

  // 強制重載所有廣告
  forceReloadAllAds() {
    this.log('🔄 強制重載所有廣告...', 'info')
    
    // 清除現有狀態
    const adElements = document.querySelectorAll('.adsbygoogle')
    adElements.forEach(el => {
      el.removeAttribute('data-adsbygoogle-status')
      el.innerHTML = ''
    })
    
    // 重新推送到 AdSense
    if ((window as any).adsbygoogle) {
      try {
        adElements.forEach(() => {
          (window as any).adsbygoogle.push({})
        })
        this.log(`✅ 重新推送 ${adElements.length} 個廣告`, 'success')
      } catch (error) {
        this.log('❌ 重載廣告失敗: ' + error, 'error')
      }
    } else {
      this.log('❌ AdSense 腳本未載入', 'error')
    }
  }
}

// 自動初始化（僅在瀏覽器環境）
if (typeof window !== 'undefined') {
  const debugConsole = AdDebugConsole.getInstance()
  
  // 頁面載入完成後初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      debugConsole.initializeDebugConsole()
    })
  } else {
    debugConsole.initializeDebugConsole()
  }
}

export default AdDebugConsole 